// src/lib/coopMembers.js
// Cooperative Membership utilities and types

import { 
  coopMembershipSchema, 
  coopMembershipApplicationSchema,
  coopMembershipStatusSchema,
  coopMembershipClassSchema 
} from './validation/schemas.js';

/**
 * Cooperative Membership Status Constants
 */
export const COOP_STATUS = {
  NONE: 'none',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SUSPENDED: 'suspended'
};

/**
 * Cooperative Membership Class Constants
 */
export const COOP_CLASS = {
  FARMER: 'farmer',
  CONSUMER: 'consumer',
  ASSOCIATE: 'associate',
  INSTITUTION: 'institution'
};

/**
 * Check if a user has approved cooperative membership
 * @param {Object} coopMember - The coopMembers document data
 * @returns {boolean} - True if user has approved membership
 */
export function isApprovedCoopMember(coopMember) {
  return coopMember && coopMember.status === COOP_STATUS.APPROVED;
}

/**
 * Check if a user has pending cooperative membership
 * @param {Object} coopMember - The coopMembers document data
 * @returns {boolean} - True if user has pending membership
 */
export function isPendingCoopMember(coopMember) {
  return coopMember && coopMember.status === COOP_STATUS.PENDING;
}

/**
 * Check if a user has any cooperative membership (including pending/rejected)
 * @param {Object} coopMember - The coopMembers document data
 * @returns {boolean} - True if user has any membership record
 */
export function hasCoopMembership(coopMember) {
  return coopMember && coopMember.status !== COOP_STATUS.NONE;
}

/**
 * Get membership status display text
 * @param {string} status - The membership status
 * @returns {string} - Human-readable status text
 */
export function getMembershipStatusText(status) {
  const statusMap = {
    [COOP_STATUS.NONE]: 'No Membership',
    [COOP_STATUS.PENDING]: 'Pending Approval',
    [COOP_STATUS.APPROVED]: 'Approved Member',
    [COOP_STATUS.REJECTED]: 'Application Rejected',
    [COOP_STATUS.SUSPENDED]: 'Membership Suspended'
  };
  return statusMap[status] || 'Unknown Status';
}

/**
 * Get membership class display text
 * @param {string} membershipClass - The membership class
 * @returns {string} - Human-readable class text
 */
export function getMembershipClassText(membershipClass) {
  const classMap = {
    [COOP_CLASS.FARMER]: 'Farmer',
    [COOP_CLASS.CONSUMER]: 'Consumer',
    [COOP_CLASS.ASSOCIATE]: 'Associate',
    [COOP_CLASS.INSTITUTION]: 'Institution'
  };
  return classMap[membershipClass] || 'Unknown Class';
}

/**
 * Get membership status badge color class
 * @param {string} status - The membership status
 * @returns {string} - Tailwind CSS color classes
 */
export function getMembershipStatusBadgeClass(status) {
  const badgeMap = {
    [COOP_STATUS.NONE]: 'bg-gray-100 text-gray-800 border-gray-200',
    [COOP_STATUS.PENDING]: 'bg-amber-100 text-amber-800 border-amber-200',
    [COOP_STATUS.APPROVED]: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    [COOP_STATUS.REJECTED]: 'bg-rose-100 text-rose-800 border-rose-200',
    [COOP_STATUS.SUSPENDED]: 'bg-red-100 text-red-800 border-red-200'
  };
  return badgeMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
}

/**
 * Validate cooperative membership data
 * @param {Object} data - The data to validate
 * @returns {Object} - Validation result with success and data/errors
 */
export function validateCoopMembership(data) {
  try {
    const validated = coopMembershipSchema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    return { success: false, errors: error.errors };
  }
}

/**
 * Validate cooperative membership application data
 * @param {Object} data - The data to validate
 * @returns {Object} - Validation result with success and data/errors
 */
export function validateCoopMembershipApplication(data) {
  try {
    const validated = coopMembershipApplicationSchema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    return { success: false, errors: error.errors };
  }
}

/**
 * Create initial cooperative membership document
 * @param {string} uid - User ID
 * @param {Object} applicationData - Application data
 * @returns {Object} - Initial membership document
 */
export function createInitialCoopMembership(uid, applicationData) {
  const now = new Date();
  
  return {
    uid,
    class: applicationData.class,
    status: COOP_STATUS.PENDING,
    verifiedByAdmin: false,
    kycDocs: applicationData.kycDocs || {},
    dues: {
      entranceFeePaid: applicationData.entranceFee || false,
      shares: applicationData.shareCapital || 0,
      savings: 0
    },
    createdAt: now,
    updatedAt: now,
    applicationNotes: applicationData.notes || '',
    emergencyContact: {
      name: applicationData.emergencyContactName || '',
      phone: applicationData.emergencyContactPhone || '',
      relationship: applicationData.emergencyContactRelationship || ''
    }
  };
}

/**
 * Check if user can access cooperative features
 * @param {Object} userProfile - User profile from useUserProfile
 * @param {Object} coopMember - Cooperative membership data
 * @returns {boolean} - True if user can access coop features
 */
export function canAccessCoopFeatures(userProfile, coopMember) {
  // Staff members always have access (now using cooperative member roles)
  // This function will need to be updated to use the new role system
  // For now, we'll keep the legacy check but it should be updated
  if (userProfile?.roles?.includes('admin') || 
      userProfile?.roles?.includes('treasurer') ||
      userProfile?.roles?.includes('manager')) {
    return true;
  }
  
  // Regular users need approved membership
  return isApprovedCoopMember(coopMember);
}

/**
 * Get membership benefits based on class and status
 * @param {string} membershipClass - The membership class
 * @param {string} status - The membership status
 * @returns {Array} - Array of benefit descriptions
 */
export function getMembershipBenefits(membershipClass, status) {
  if (status !== COOP_STATUS.APPROVED) {
    return ['Membership approval required to access benefits'];
  }
  
  const benefits = {
    [COOP_CLASS.FARMER]: [
      'Access to agricultural inputs at member prices',
      'Participation in cooperative programs',
      'Voting rights in cooperative decisions',
      'Access to financial services'
    ],
    [COOP_CLASS.CONSUMER]: [
      'Access to products at member prices',
      'Participation in cooperative programs',
      'Voting rights in cooperative decisions'
    ],
    [COOP_CLASS.ASSOCIATE]: [
      'Access to products at member prices',
      'Participation in cooperative programs'
    ],
    [COOP_CLASS.INSTITUTION]: [
      'Bulk purchasing benefits',
      'Institutional partnership opportunities',
      'Access to cooperative programs'
    ]
  };
  
  return benefits[membershipClass] || ['Standard member benefits'];
}
